import { mergeQueryParams } from '@/utils/merge-query-params';
import { Params } from '@/utils/types';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { SWAP_REASONS } from '../measurement-group/field-report/types';

export default function useFieldReportsFilters() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const setDebouncedQueryParam = useDebouncedCallback(search => {
		if (!search) {
			const queryString = mergeQueryParams(searchParams, {
				[Params.search]: undefined,
				[Params.page]: 1
			});

			navigate(`?${queryString}`);
			return;
		}
		const queryString = mergeQueryParams(searchParams, {
			[Params.search]: search,
			[Params.page]: 1
		});

		navigate(`?${queryString.toString()}`);
	}, 500);

	const handleSearchChange = useCallback(
		(search: string) => {
			setDebouncedQueryParam(search);
		},
		[setDebouncedQueryParam]
	);

	const handleDeploymentTypeChange = useCallback(
		(type: string) => {
			if (!type || type === 'All') {
				const queryString = mergeQueryParams(searchParams, {
					[Params.deploymentType]: undefined,
					[Params.page]: 1
				});

				navigate(`?${queryString}`);
				return;
			}
			const queryString = mergeQueryParams(searchParams, {
				[Params.deploymentType]: type,
				[Params.page]: 1
			});

			navigate(`?${queryString.toString()}`);
		},
		[navigate, searchParams]
	);

	const handleSwapReasonChange = useCallback(
		(swapReason: SWAP_REASONS | 'All' ) => {
			if (!swapReason || swapReason === 'All') {
				const queryString = mergeQueryParams(searchParams, {
					[Params.swapReason]: undefined,
					[Params.page]: 1
				});

				navigate(`?${queryString}`);
				return;
			}
			const queryString = mergeQueryParams(searchParams, {
				[Params.swapReason]: swapReason,
				[Params.page]: 1
			});

			navigate(`?${queryString.toString()}`);
		},
		[navigate, searchParams]
	);

	const handleHasExternalAntennasChange = useCallback(
		(hasExternalAntennas: 'true' | 'false' | 'N/A') => {
			if (!hasExternalAntennas || hasExternalAntennas === 'N/A') {
				const queryString = mergeQueryParams(searchParams, {
					[Params.hasExternalAntennas]: undefined,
					[Params.page]: 1
				});

				navigate(`?${queryString}`);
				return;
			}
			const queryString = mergeQueryParams(searchParams, {
				[Params.hasExternalAntennas]: hasExternalAntennas,
				[Params.page]: 1
			});

			navigate(`?${queryString.toString()}`);
		},
		[navigate, searchParams]
	);

	return { handleSearchChange, handleDeploymentTypeChange, handleSwapReasonChange, handleHasExternalAntennasChange };
}
